/**
 * reuseEngine.js — recommendation + spaced-retrieval engine. Runs fully
 * offline from local learner data and the content registry.
 * Every item can appear in Today, Learn, Explore, Play, reteach, review
 * and parent planning without duplication.
 */
import { loadMastery } from "@/lib/mastery";
import { loadProgress } from "@/lib/progress";
import { getContinueReading } from "@/lib/storyProgress";
import { listEntries } from "@/lib/portfolio";

export const REVIEW_STAGES = [7, 21, 45];
const DAY = 86400000;
const REVIEW_KEY = "mwq_reviews_v1"; // { [code]: [isoDate,...] }

function reviews() { try { return JSON.parse(localStorage.getItem(REVIEW_KEY) || "{}"); } catch { return {}; } }
export function markReviewed(code) {
  const r = reviews(); r[code] = [...(r[code] || []), new Date().toISOString()].slice(-5);
  try { localStorage.setItem(REVIEW_KEY, JSON.stringify(r)); } catch { /* ignore */ }
}

/** Mastered outcomes due for retrieval at 7 / 21 / 45 days. Never punitive — just "ready to review". */
export function reviewDue(now = Date.now()) {
  const m = loadMastery(); const r = reviews(); const due = [];
  for (const [code, rec] of Object.entries(m)) {
    if (!rec.mastered || !rec.attempts?.length) continue;
    const masteredAt = new Date(rec.attempts[rec.attempts.length - 1].dateISO).getTime();
    const done = (r[code] || []).length;
    const stage = REVIEW_STAGES[done];
    if (stage && now - masteredAt >= stage * DAY) due.push({ code, stage, daysSince: Math.floor((now - masteredAt) / DAY) });
  }
  return due.sort((a, b) => b.daysSince - a.daysSince);
}

export function stillGrowingCodes() {
  const m = loadMastery();
  return Object.entries(m).filter(([, r]) => !r.mastered && r.attempts?.length && r.attempts[r.attempts.length - 1].score < 4).map(([c]) => c);
}

export function learnerSnapshot() {
  const p = loadProgress();
  let diag = {}; try { diag = JSON.parse(localStorage.getItem("mwq_diag_results_v1") || "{}"); } catch { /* ignore */ }
  return { stars: p.stars, countries: p.countriesLearned || [], activity: (p.activity || []).slice(-30), mastered: Object.values(loadMastery()).filter((r) => r.mastered).length,
    growing: stillGrowingCodes(), due: reviewDue(), story: getContinueReading(), portfolio: listEntries().length, diagnostics: diag,
    offline: typeof navigator !== "undefined" && navigator.onLine === false };
}

const pick = (arr, seed) => (arr.length ? arr[seed % arr.length] : null);
const daySeed = () => Math.floor(Date.now() / DAY);

/**
 * Decide the homepage state and one recommended quest (+ one alternative).
 * States: new | returning | unfinished | reteach | stretch | offline
 */
export function recommend(registry, { tier = "explorers", interests = [] } = {}) {
  const snap = learnerSnapshot();
  const seed = daySeed();
  const forTier = (arr) => arr.filter((i) => i.tiers.includes(tier) && i.status === "live");
  const lessons = forTier(registry.items.filter((i) => i.type === "lesson"));
  const projects = forTier(registry.items.filter((i) => i.type === "project"));
  const games = forTier(registry.items.filter((i) => i.type === "game" && !i.timed));
  const countries = forTier(registry.items.filter((i) => i.type === "country"));
  const story = registry.byId["story:hub"];
  let unfinished = null; try { unfinished = JSON.parse(localStorage.getItem("mwq_unfinished_v1") || "null"); } catch { /* ignore */ }
  let unitWork = false; try { unitWork = Object.keys(JSON.parse(localStorage.getItem("mwq_unit_progress_v1") || "{}")).length > 0; } catch { /* ignore */ }
  const isNew = !unfinished && !unitWork && snap.portfolio === 0 && snap.activity.length === 0 && snap.mastered === 0 && snap.countries.length === 0;

  let state = "returning";
  let primary = null; let why = "";

  if (snap.offline) state = "offline";
  if (isNew) {
    state = "new";
    primary = registry.byId["game:first-quest"] || pick(lessons, seed);
    why = "A gentle first adventure — no reading pressure, just discovering the world.";
  } else if (unfinished?.to) {
    state = "unfinished";
    primary = { ...(registry.byId[unfinished.id] || {}), ...unfinished, title: unfinished.title, to: unfinished.to, minutes: unfinished.minutes || 8 };
    why = "You were part-way through this — picking up where you left off is the fastest way to grow.";
  } else if (snap.growing.length) {
    state = "reteach";
    const code = snap.growing[0];
    primary = { id: `reteach:${code}`, title: "Try a new way", description: `A short reteach for ${code}`, type: "lesson", subject: "Review", minutes: 6,
      to: `/practice/${code}`, objective: "Make a growing skill secure", skill: code };
    why = "One skill is still growing. A short practice with a new explanation will help it click.";
  } else if (snap.due.length) {
    state = "review";
    const d = snap.due[0];
    primary = { id: `review:${d.code}`, title: "Quick memory check", description: `Revisit ${d.code} (${d.stage}-day review)`, type: "quiz", subject: "Review", minutes: 4,
      to: `/practice/${d.code}`, objective: "Strengthen memory with spaced retrieval", skill: d.code, reviewCode: d.code };
    why = `You mastered this ${d.daysSince} days ago — a quick revisit locks it in for good.`;
  } else if (snap.mastered >= 3 && projects.length) {
    state = "stretch";
    primary = pick(projects, seed);
    why = "You're ready for a challenge — apply what you know to a real project.";
  } else {
    primary = pick(lessons, seed) || pick(countries, seed);
    why = "A fresh lesson matched to your stage, balancing the subjects you've touched this week.";
  }
  if (snap.offline && primary && primary.offline === false) { primary = pick(games, seed) || pick(countries, seed); why = "You're offline — this one works without internet."; }

  const alternative = pick(countries.filter((c) => !snap.countries.includes(c.id.split(":")[1])), seed + 3) || pick(games, seed);
  const curiosity = pick(CURIOSITY, seed);
  const exploreMore = [pick(countries, seed + 11), pick(games, seed + 5), story].filter(Boolean).slice(0, 3);
  const cont = unfinished?.to ? unfinished : snap.story ? { title: "Continue your story", to: "/stories", minutes: 8 } : null;
  const progressLine = snap.countries.length ? `You have explored ${snap.countries.length} place${snap.countries.length === 1 ? "" : "s"} so far.`
    : snap.growing.length ? "One skill is ready to grow." : snap.mastered ? `${snap.mastered} skill${snap.mastered === 1 ? "" : "s"} secure. Ready when you are.` : "Your world is waiting. Ready when you are.";
  return { state, primary, why, alternative, curiosity, exploreMore, continue: cont, progressLine, snapshot: snap };
}

export function setUnfinished(item) { try { localStorage.setItem("mwq_unfinished_v1", JSON.stringify(item)); } catch { /* ignore */ } }
export function clearUnfinished() { try { localStorage.removeItem("mwq_unfinished_v1"); } catch { /* ignore */ } }

export const CURIOSITY = [
  { q: "Why do volcanoes erupt?", to: "/explore/nature" }, { q: "Which country has the most languages?", to: "/learn-countries" },
  { q: "How do bees know where the flowers are?", to: "/animals" }, { q: "Why is the sea salty?", to: "/explore/nature" },
  { q: "Who built the pyramids — and how?", to: "/explore/wonders" }, { q: "How does a rainbow happen?", to: "/learn-topics?s=science" },
  { q: "Why do we have leap years?", to: "/learn-topics?s=maths" }, { q: "What is the coldest place people live?", to: "/learn-countries" },
];

const ASSIGN_KEY = "mwq_assignments_v1";
export function listAssignments() { try { return JSON.parse(localStorage.getItem(ASSIGN_KEY) || "[]"); } catch { return []; } }
export function saveAssignments(list) { try { localStorage.setItem(ASSIGN_KEY, JSON.stringify(list)); window.dispatchEvent(new Event("mwq-assignments-changed")); } catch { /* ignore */ } }
